import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, ShieldCheck, User, LogOut } from "lucide-react";
import { CompareTray } from "./CompareTray";
import { LOGO_NAV, LOGO_FULL } from "../lib/vehicleImages";
import { useSearch } from "../context/SearchContext";
import { useAuth } from "../context/AuthContext";
import { useMarket } from "../context/MarketContext";
import { Button } from "./ui/button";
import {
  Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle,
} from "./ui/sheet";

const LINKS = [
  { to: "/intelligence", label: "Find Opportunities" },
  { to: "/coverage", label: "Market Intelligence" },
  { to: "/tools", label: "Tools" },
  { to: "/trust", label: "Resources" },
  { to: "/list-your-source", label: "For Dealers" },
];

const LANGUAGES = ["EN", "DE", "NL", "FR", "ES", "IT"];
const LanguageSelector = ({ mobile }) => {
  const [lang, setLang] = React.useState(() => localStorage.getItem("atlas_lang") || "EN");
  return (
    <label className={mobile ? "flex flex-col gap-1" : "flex items-center gap-1.5"} title="Interface language">
      <span className={mobile ? "text-sm text-slate-300" : "text-[11px] text-slate-400 hidden xl:inline"}>Language</span>
      <select value={lang} data-testid="language-selector"
        onChange={(e) => { setLang(e.target.value); localStorage.setItem("atlas_lang", e.target.value); }}
        className={`rounded-sm bg-white/5 border border-white/15 text-slate-200 text-xs px-2 ${mobile ? "h-10 w-full" : "h-8"}`}>
        {LANGUAGES.map((l) => <option key={l} value={l} className="text-black">{l}</option>)}
      </select>
    </label>
  );
};

const slug = (label) => label.toLowerCase().replace(/\s+/g, "-");

const NavLink = ({ to, label, active, count }) => (
  <Link
    to={to}
    data-testid={`nav-${slug(label)}`}
    className={`relative text-sm font-medium transition-colors ${
      active ? "text-white" : "text-slate-300 hover:text-white"
    }`}
  >
    {label}
    {count > 0 && (
      <span className="ml-1.5 font-mono text-[10px] px-1.5 py-0.5 rounded-sm bg-atlas-blue text-white">{count}</span>
    )}
    {active && <span className="absolute -bottom-[21px] left-0 right-0 h-0.5 bg-atlas-sky" />}
  </Link>
);

const AccountArea = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (user === null) return <div className="w-24" />; // checking
  if (!user) {
    return (
      <div className="flex items-center gap-3 pl-6 border-l border-white/15">
        <Link to="/login" data-testid="nav-signin" className="text-sm font-medium text-slate-300 hover:text-white">Sign in</Link>
        <Button onClick={() => navigate("/register")} data-testid="nav-register"
          className="bg-atlas-blue hover:bg-atlas-sky text-white h-9 px-4 text-sm">Get Started</Button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 pl-6 border-l border-white/15">
      <Link to="/account" className="flex items-center gap-1.5 text-sm text-slate-200 hover:text-white" data-testid="nav-account-name">
        <User className="h-4 w-4 text-atlas-sky" /> {user.name || user.email}
      </Link>
      <button onClick={logout} data-testid="nav-logout" title="Sign out"
        className="text-slate-400 hover:text-white"><LogOut className="h-4 w-4" /></button>
    </div>
  );
};

const VerifyBanner = () => {
  const { user } = useAuth();
  if (!user || user.email_verified) return null;
  return (
    <div className="bg-amber-50 border-b border-amber-200" data-testid="verify-banner">
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-2.5 flex items-center justify-between gap-3">
        <p className="text-sm text-amber-900">
          Please verify your email to fully activate your account.
        </p>
        <Link to="/verify-email" data-testid="verify-banner-link"
          className="text-sm font-medium text-amber-900 underline whitespace-nowrap">Verify now</Link>
      </div>
    </div>
  );
};

const MobileAccount = () => {
  const { user, logout } = useAuth();
  if (user) {
    return (
      <div className="flex flex-col gap-3">
        <span className="text-sm text-slate-300 flex items-center gap-2"><User className="h-4 w-4" /> {user.name || user.email}</span>
        <SheetClose asChild>
          <Link to="/account" data-testid="mobile-nav-account" className="text-lg font-medium text-slate-200 hover:text-white">My account</Link>
        </SheetClose>
        <SheetClose asChild>
          <button onClick={logout} data-testid="mobile-nav-logout" className="text-left text-lg font-medium text-slate-200 hover:text-white">Sign out</button>
        </SheetClose>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <SheetClose asChild>
        <Link to="/login" data-testid="mobile-nav-signin" className="text-lg font-medium text-slate-200 hover:text-white">Sign in</Link>
      </SheetClose>
      <SheetClose asChild>
        <Link to="/register" data-testid="mobile-nav-register" className="text-lg font-medium text-atlas-sky">Create account</Link>
      </SheetClose>
    </div>
  );
};

const MarketSelector = ({ mobile }) => {
  const { market, setMarket, markets } = useMarket();
  return (
    <label className={mobile ? "flex flex-col gap-1" : "flex items-center gap-1.5"}
      title="Prices are shown in your market's currency">
      <span className={mobile ? "text-sm text-slate-300" : "text-[11px] text-slate-400 hidden lg:inline"}>Market</span>
      <select
        value={market.code}
        onChange={(e) => setMarket(e.target.value)}
        data-testid="market-selector"
        className={`rounded-sm bg-white/5 border border-white/15 text-slate-200 text-xs px-2 ${mobile ? "h-10 w-full" : "h-8"}`}
      >
        {markets.map((m) => (
          <option key={m.code} value={m.code} className="text-black">{m.label} · {m.currency}</option>
        ))}
      </select>
    </label>
  );
};

export const Layout = ({ children }) => {
  const { pathname } = useLocation();
  const { compareIds } = useSearch();

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-40 bg-atlas-navy border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="brand-home" className="flex items-center gap-3.5">
            <img src={LOGO_NAV} alt="ATLAS" className="h-9 md:h-11 w-auto" />
            <span className="hidden xl:flex flex-col border-l border-white/15 pl-3.5 leading-tight">
              <span className="text-[13px] font-medium text-slate-200">Find better.</span>
              <span className="text-[13px] font-medium text-atlas-sky">Compare smarter.</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-6">
            {LINKS.map((l) => (
              <NavLink key={l.to} {...l} active={pathname === l.to}
                count={l.label === "Compare" ? compareIds.length : 0} />
            ))}
            <LanguageSelector />
            <MarketSelector />
            <AccountArea />
          </nav>

          <Sheet>
            <SheetTrigger className="lg:hidden text-slate-200" data-testid="mobile-menu-btn" aria-label="Menu">
              <Menu className="h-6 w-6" />
            </SheetTrigger>
            <SheetContent side="right" className="bg-atlas-navy border-white/10 text-white overflow-y-auto">
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <img src={LOGO_FULL} alt="ATLAS" className="h-16 w-auto mb-2" />
              <p className="text-sm text-atlas-sky mb-8">Find better. Compare smarter.</p>
              <div className="flex flex-col gap-5">
                {LINKS.map((l) => (
                  <SheetClose asChild key={l.to}>
                    <Link to={l.to} data-testid={`mobile-nav-${slug(l.label)}`}
                      className="text-lg font-medium text-slate-200 hover:text-white">{l.label}</Link>
                  </SheetClose>
                ))}
                <div className="border-t border-white/10 pt-5 mt-1 grid grid-cols-2 gap-3">
                  <LanguageSelector mobile />
                  <MarketSelector mobile />
                </div>
                <div className="border-t border-white/10 pt-5 mt-1">
                  <MobileAccount />
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <VerifyBanner />
      <main className="flex-1">{children}</main>

      <footer className="bg-atlas-night text-slate-400 mt-24">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-14">
          <div className="grid gap-10 lg:gap-8 md:grid-cols-2 lg:grid-cols-6">
            <div className="lg:col-span-2">
              <img src={LOGO_FULL} alt="ATLAS" className="h-14 w-auto mb-3" />
              <p className="text-sm text-atlas-sky mb-3">European Vehicle Acquisition Intelligence</p>
              <p className="text-sm text-slate-500 max-w-xs leading-relaxed">
                Know what to buy, where, and what to pay. Atlas provides intelligence — it is not a
                dealership, marketplace, seller or payment intermediary.
              </p>
              <div className="mt-4 max-w-[160px]"><LanguageSelector /></div>
            </div>

            <div>
              <div className="overline text-slate-500 mb-3">Product</div>
              <div className="flex flex-col gap-2 text-sm">
                <Link to="/intelligence" className="hover:text-white" data-testid="footer-find-opportunities">Find Opportunities</Link>
                <Link to="/coverage" className="hover:text-white">Market Intelligence</Link>
                <Link to="/tools" className="hover:text-white">Cost Calculator</Link>
                <Link to="/intelligence" className="hover:text-white">Opportunity Reports</Link>
                <Link to="/list-your-source" className="hover:text-white">For Dealers</Link>
              </div>
            </div>

            <div>
              <div className="overline text-slate-500 mb-3">Resources</div>
              <div className="flex flex-col gap-2 text-sm">
                <Link to="/#how" className="hover:text-white">How Atlas Works</Link>
                <Link to="/coverage" className="hover:text-white">Market Insights</Link>
                <Link to="/coverage" className="hover:text-white">European Markets</Link>
                <Link to="/trust" className="hover:text-white">Import Guides</Link>
                <Link to="/trust" className="hover:text-white">FAQ</Link>
              </div>
            </div>

            <div>
              <div className="overline text-slate-500 mb-3">Company</div>
              <div className="flex flex-col gap-2 text-sm">
                <Link to="/trust" className="hover:text-white">About Atlas</Link>
                <Link to="/report" className="hover:text-white">Contact</Link>
                <Link to="/legal/terms" data-testid="footer-terms" className="hover:text-white">Terms of Use</Link>
                <Link to="/legal/privacy" data-testid="footer-privacy" className="hover:text-white">Privacy Notice</Link>
                <Link to="/legal/data-sources" data-testid="footer-data-sources" className="hover:text-white">Data &amp; Sources</Link>
              </div>
            </div>

            <div>
              <div className="overline text-slate-500 mb-3">Markets</div>
              <div className="flex flex-col gap-2 text-sm">
                {[["DE", "Germany"], ["NL", "Netherlands"], ["ES", "Spain"], ["BE", "Belgium"], ["FR", "France"], ["IT", "Italy"]].map(([c, n]) => (
                  <Link key={c} to={`/atlas?market=${c}`} className="hover:text-white">{n}</Link>
                ))}
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 mt-10 pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-500">
              <span className="text-slate-400 font-medium">Popular searches:</span>
              {[["BMW", "3 Series"], ["Mercedes-Benz", "C-Class"], ["Volkswagen", "Golf"], ["Audi", "A4"], ["Toyota", "RAV4"]].map(([mk, md]) => (
                <Link key={md} to={`/atlas?make=${encodeURIComponent(mk)}&model=${encodeURIComponent(md)}`} className="hover:text-white">{mk} {md}</Link>
              ))}
            </div>
            <p className="text-xs text-slate-600 font-mono">© {new Date().getFullYear()} Atlas · Europe</p>
          </div>
          <p className="text-xs text-slate-600 font-mono mt-4 leading-relaxed">
            Figures shown across Atlas are computed from operator-entered data and real ECB reference FX rates. Legal drafts pending Netherlands/EU review.
          </p>
        </div>
      </footer>

      <CompareTray />
    </div>
  );
};
